/**
 * phasePositions — the position-assignment scheme shared by both REORDER paths
 * into `UpdateKanbanPhasePositions` (SE-6535): the Stages editor panel and the
 * board's drag-and-drop.
 *
 * It is not the only writer of that mutation. `calculatePhasePositionsAfterDelete`
 * (in `hooks/useKanbanPhaseOperations.ts`) builds its own payload and is safe by
 * a DIFFERENT invariant — it anchors every assigned position strictly above the
 * board's current maximum, so no live position can be re-issued. That invariant
 * is documented at its definition; do not assume this module covers it.
 *
 * `KanbanPhase` carries a unique constraint on `(kanbanBoardId, position)`, and
 * the backend applies a reorder as sequential row updates inside a single
 * transaction with a non-deferred constraint. A payload is therefore only safe
 * if every position it assigns is free at the moment it is written — it is not
 * enough for the FINAL state to be conflict-free.
 *
 * The old Stage-editor scheme assigned `(index + 1) * GAP` on every call, which
 * re-issues the exact positions the board already holds: swapping two adjacent
 * stages sets stage A to the position stage B still holds, Postgres raises
 * 23505, and the whole reorder rolls back as a generic "An error occurred"
 * toast. Board drag-and-drop dodged this by rebasing onto a large random offset.
 *
 * This module makes that dodge explicit and shared, and upgrades it from
 * probabilistic to guaranteed:
 * 1. Start from a random base in `[MIN, MIN + SPAN)`, like the board path.
 * 2. Deterministically shift that base past any position the board currently
 *    holds, so the assigned window is provably DISJOINT from the live set.
 *
 * Because every new position is outside the live set and the assigned positions
 * are distinct, no intermediate state of the transaction can ever collide —
 * regardless of the order the backend applies the updates in.
 * @module features/player-kanban/utils/phasePositions
 */

import * as Crypto from "expo-crypto";

/** A `{ phaseId, position }` entry for `UpdateKanbanPhasePositions`. */
export interface StagePosition {
  readonly phaseId: string;
  readonly position: number;
}

/**
 * Lowest base a rebase may start from. Kept well above the legacy gapped
 * positions (`n * 100000`) so a first rebase off a legacy board lands clear of
 * them without needing the disjointness shift.
 */
export const PHASE_POSITION_BASE_MIN = 1_000_000;

/** Width of the random base window, so bases fall in `[1M, 1B)`. */
export const PHASE_POSITION_BASE_SPAN = 999_000_000;

/** `position` is a Postgres `int4`; a rebase must never exceed this. */
export const PHASE_POSITION_MAX = 2_147_483_647;

/**
 * Draw a cryptographically-random base position in
 * `[PHASE_POSITION_BASE_MIN, PHASE_POSITION_BASE_MIN + PHASE_POSITION_BASE_SPAN)`.
 *
 * Randomising the base (rather than climbing monotonically) keeps positions
 * bounded well under `PHASE_POSITION_MAX` no matter how many times a board is
 * reordered.
 * @returns A base position, comfortably inside the 32-bit signed range.
 */
export const randomPhasePositionBase = (): number => {
  const bytes = Crypto.getRandomBytes(4);
  const value =
    ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
  return (value % PHASE_POSITION_BASE_SPAN) + PHASE_POSITION_BASE_MIN;
};

/**
 * Shift a candidate base upward until `[base, base + count)` holds none of the
 * given ascending positions.
 *
 * One pass suffices. For each occupied position exactly one of three things is
 * true afterwards: it was inside the window and pushed the base one past it (so
 * it now sits strictly below the window, and the base only ever rises); it was
 * already below the window (and stays below); or it was at or beyond
 * `base + count`, in which case so is every later position, and the base can
 * never rise again. The ascending order is what makes the third case hold.
 * @param start - The base to start from.
 * @param count - How many consecutive positions the payload needs.
 * @param ascendingPositions - Occupied positions, sorted ascending.
 * @returns The lowest base at or above `start` whose window is disjoint.
 */
const shiftBaseClear = (
  start: number,
  count: number,
  ascendingPositions: readonly number[]
): number =>
  ascendingPositions.reduce(
    (candidate, position) =>
      position >= candidate && position < candidate + count
        ? position + 1
        : candidate,
    start
  );

/**
 * Resolve a base whose `count`-wide window holds none of the board's current
 * positions.
 *
 * Shifting upward can in principle run out of `int4` headroom. It never does on
 * a real board — reaching it would need occupied positions stepping to the
 * `int4` ceiling in strides narrower than `count`, i.e. hundreds of millions of
 * stages — but the fallback must not hand back a window known to overlap, so it
 * rescans from the bottom of the band instead. Only a fully packed position
 * space can defeat both, and that throws rather than emitting a payload
 * guaranteed to raise 23505.
 * @param preferredBase - The base to start from (typically random).
 * @param count - How many consecutive positions the payload needs.
 * @param currentPositions - Every position the board currently holds.
 * @returns A base whose `count`-wide window is disjoint from `currentPositions`.
 * @throws {Error} When no disjoint window exists below `PHASE_POSITION_MAX`.
 */
export const resolveCollisionFreeBase = (
  preferredBase: number,
  count: number,
  currentPositions: readonly number[]
): number => {
  if (count <= 0) return preferredBase;

  const occupied = Array.from(new Set(currentPositions)).sort((a, b) => a - b);
  // The highest assigned position is `base + count - 1`, not `base + count`.
  const fits = (base: number): boolean =>
    base + count - 1 <= PHASE_POSITION_MAX;

  const base = shiftBaseClear(preferredBase, count, occupied);
  if (fits(base)) return base;

  const wrapped = shiftBaseClear(PHASE_POSITION_BASE_MIN, count, occupied);
  if (fits(wrapped)) return wrapped;

  throw new Error(
    `No collision-free window of ${count} positions exists below ${PHASE_POSITION_MAX}`
  );
};

/** Options for {@link computePhasePositions}. */
export interface ComputePhasePositionsOptions {
  /**
   * Every position the board currently holds. Supplying this is what makes the
   * payload provably collision-free rather than merely improbable — omit it and
   * the scheme degrades to the board path's probabilistic guarantee.
   */
  readonly currentPositions?: readonly number[];
  /**
   * Base to rebase onto before the disjointness shift. Defaults to
   * {@link randomPhasePositionBase}; tests inject a fixed value to assert the
   * scheme deterministically.
   */
  readonly baseHint?: number;
}

/**
 * Assign collision-safe positions to an ordered list of phase ids.
 *
 * The result is the exact `PhasePosition[]` payload for
 * `UpdateKanbanPhasePositions`, which persists the canonical `position` the
 * board's PIPELINE sort reads back. Positions are consecutive from a rebased
 * origin — the gap the old scheme reserved for later insertions is unnecessary
 * because an insertion appends above the current maximum and any reorder
 * rebases the whole board anyway.
 * @param orderedPhaseIds - Phase ids in their intended visual order.
 * @param options - {@link ComputePhasePositionsOptions}.
 * @param options.currentPositions - The board's live positions (collision set).
 * @param options.baseHint - Fixed base to rebase onto, for deterministic tests.
 * @returns The positions payload, one entry per id, in the given order.
 */
export const computePhasePositions = (
  orderedPhaseIds: readonly string[],
  { currentPositions = [], baseHint }: ComputePhasePositionsOptions = {}
): StagePosition[] => {
  if (orderedPhaseIds.length === 0) return [];

  const base = resolveCollisionFreeBase(
    baseHint ?? randomPhasePositionBase(),
    orderedPhaseIds.length,
    currentPositions
  );

  return orderedPhaseIds.map((phaseId, index) => ({
    phaseId,
    position: base + index,
  }));
};
